/**
 * RitualVideoPlayer (issue #362)
 *
 * Discriminates between two playback paths based on the RitualVideo row
 * shape:
 *
 *   - Cloudflare-hosted (cloudflareUid != null, status="ready") -> render
 *     the official Cloudflare <Stream> React component. For PUBLIC rituals
 *     pass the bare UID; for PRIVATE/PERSONAL pass a signed JWT fetched
 *     from /api/rituals/:id/videos/:videoId/playback-token. The CF player
 *     does NOT auto-renew tokens, so on onError we re-fetch and re-mount
 *     with a fresh `src` (preserving currentTime via the imperative ref).
 *
 *   - Legacy URL (videoUrl != null) -> keep the existing react-player +
 *     getEmbedUrl path unchanged.
 *
 * Non-ready Cloudflare rows render a placeholder reflecting their status.
 * Failed rows surface the failureReason. The component is read-only -- it
 * does NOT drive uploads, retries, or deletes; that's the uploader's job.
 */

import { Stream, type StreamPlayerApi } from "@cloudflare/stream-react"
import { useCallback, useEffect, useRef, useState } from "react"
import ReactPlayer from "react-player"
import { useTranslation } from "@/language"
import {
  getEmbedUrl,
  getRitualVideoPlaybackToken,
  type RitualVideo,
} from "../services/ritual.service"

export interface RitualVideoPlayerProps {
  ritualId: string
  video: RitualVideo
  /** PRIVATE / PERSONAL rituals need a signed JWT; PUBLIC use the bare UID. */
  requiresSignedToken: boolean
  className?: string
  onPlay?: () => void
  onPause?: () => void
  onEnded?: () => void
  /** When false, the video is paused. Use in carousels to stop playback when
   *  the user navigates to another slide. Defaults to true. */
  active?: boolean
}

const isReadyForPlayback = (video: RitualVideo): boolean =>
  video.status === "ready" && (video.cloudflareUid !== null || video.videoUrl !== null)

const NonReadyPlaceholder = ({ video }: { video: RitualVideo }) => {
  const { t } = useTranslation("rituals")
  if (video.status === "failed") {
    return (
      <div className="w-full h-full flex items-center justify-center bg-black text-white p-8">
        <div className="text-center">
          <p className="text-lg font-semibold mb-2">{t("videoPlayer.videoUnavailable")}</p>
          <p className="text-sm opacity-75">
            {video.failureReason ?? "Processing failed. The owner can re-upload."}
          </p>
        </div>
      </div>
    )
  }
  const label =
    video.status === "pending"
      ? "Queued for processing"
      : video.status === "processing"
        ? "Adding branded intro"
        : "Preparing video"
  return (
    <div className="w-full h-full flex items-center justify-center bg-black text-white">
      <p className="text-sm opacity-90">{label}...</p>
    </div>
  )
}

export function RitualVideoPlayer({
  ritualId,
  video,
  requiresSignedToken,
  className,
  onPlay,
  onPause,
  onEnded,
  active = true,
}: RitualVideoPlayerProps) {
  const MAX_TOKEN_RETRIES = 2
  const streamRef = useRef<StreamPlayerApi | undefined>(undefined)
  const lastPlaybackTimeRef = useRef<number>(0)
  const tokenRetryCountRef = useRef<number>(0)
  const cloudflarePlayerRef = useRef<HTMLDivElement>(null)
  const [signedToken, setSignedToken] = useState<string | null>(null)
  const [tokenError, setTokenError] = useState<string | null>(null)

  // Pause the stream when this slide becomes inactive (user scrolled away)
  useEffect(() => {
    if (!active && streamRef.current) {
      streamRef.current.pause()
    }
  }, [active])

  const fetchToken = useCallback(async () => {
    try {
      const result = await getRitualVideoPlaybackToken(ritualId, video.id)
      setSignedToken(result.token)
      setTokenError(null)
      tokenRetryCountRef.current = 0
    } catch (error) {
      setTokenError(error instanceof Error ? error.message : "Could not load video")
      setSignedToken(null)
    }
  }, [ritualId, video.id])

  useEffect(() => {
    if (video.cloudflareUid && requiresSignedToken) {
      tokenRetryCountRef.current = 0
      void fetchToken()
    }
  }, [video.cloudflareUid, requiresSignedToken, fetchToken])

  // The CF <Stream> component does NOT auto-renew expiring tokens. When
  // an onError fires mid-playback, capture the current playback position,
  // re-fetch a fresh JWT, then re-mount (key change triggers React to
  // remount). Capped at MAX_TOKEN_RETRIES so a persistent non-token error
  // (network outage, deleted video) can't loop forever -- after the cap
  // we surface a tokenError and stop.
  const handleStreamError = useCallback(() => {
    // currentTime can legitimately be 0 (error at the very start), so a
    // truthiness check would drop a valid resume point. Use Number.isFinite
    // to accept 0 but reject undefined/NaN.
    const t = streamRef.current?.currentTime
    if (typeof t === "number" && Number.isFinite(t)) {
      lastPlaybackTimeRef.current = t
    }
    if (
      video.cloudflareUid &&
      requiresSignedToken &&
      tokenRetryCountRef.current < MAX_TOKEN_RETRIES
    ) {
      tokenRetryCountRef.current += 1
      void fetchToken()
    } else if (tokenRetryCountRef.current >= MAX_TOKEN_RETRIES) {
      setTokenError("Playback keeps failing. Please refresh the page.")
    }
  }, [video.cloudflareUid, requiresSignedToken, fetchToken])

  // @cloudflare/stream-react@1.9.3 renders its <iframe> with an `allow`
  // permissions-policy of "accelerometer; gyroscope; autoplay;
  // encrypted-media; picture-in-picture;" — note it omits `fullscreen`. On
  // mobile browsers that gates the Fullscreen API, so the player's fullscreen
  // button is a silent no-op (worked before the Cloudflare migration when the
  // video was a native element). The component exposes no way to set `allow`,
  // so patch the iframe the moment stream-react mounts it. The MutationObserver
  // callback runs as a microtask right after the node is inserted — before the
  // iframe's load task — so the amended policy is in place when it navigates.
  useEffect(() => {
    const container = cloudflarePlayerRef.current
    if (!container) return

    const ensureFullscreen = (iframe: HTMLIFrameElement): void => {
      // Normalize rather than string-concat: don't assume Cloudflare's `allow`
      // ends in ";" (a bare last token would weld into "picture-in-picture
      // fullscreen" and void the whole policy), and match qualified forms
      // ("fullscreen 'self'") so we don't append a duplicate.
      const tokens = (iframe.getAttribute("allow") ?? "")
        .split(";")
        .map(part => part.trim())
        .filter(Boolean)
      const hasFullscreen = tokens.some(
        token => token === "fullscreen" || token.startsWith("fullscreen "),
      )
      if (!hasFullscreen) {
        tokens.push("fullscreen")
        iframe.setAttribute("allow", `${tokens.join("; ")};`)
      }
      iframe.allowFullscreen = true
    }

    const existing = container.querySelector("iframe")
    if (existing) ensureFullscreen(existing)

    const observer = new MutationObserver(() => {
      const iframe = container.querySelector("iframe")
      if (iframe) ensureFullscreen(iframe)
    })
    observer.observe(container, { childList: true, subtree: true })
    return () => observer.disconnect()
    // Re-run when the player container first mounts: for PRIVATE rituals it
    // isn't rendered until signedToken arrives, and the JWT `key` remounts the
    // <Stream> (new iframe) on rotation, which this must re-patch.
  }, [])

  if (!isReadyForPlayback(video)) {
    return (
      <div className={`w-full h-full bg-black ${className ?? ""}`}>
        <NonReadyPlaceholder video={video} />
      </div>
    )
  }

  // Cloudflare path
  if (video.cloudflareUid) {
    if (requiresSignedToken && !signedToken) {
      return (
        <div className={`w-full h-full bg-black ${className ?? ""}`}>
          <div className="w-full h-full flex items-center justify-center text-white">
            <p className="text-sm opacity-90">
              {tokenError ? `Unable to load: ${tokenError}` : "Loading playback..."}
            </p>
          </div>
        </div>
      )
    }
    const src = requiresSignedToken ? signedToken : video.cloudflareUid
    if (!src) {
      // Should be unreachable given the guard above, but keep TS happy.
      return <div className={`w-full h-full bg-black ${className ?? ""}`} />
    }
    return (
      <div ref={cloudflarePlayerRef} className={`w-full h-full bg-black ${className ?? ""}`}>
        <Stream
          // key forces a fresh mount whenever the JWT rotates, so the
          // player picks up the new src without leaking the old XHR.
          key={src}
          src={src}
          streamRef={streamRef}
          controls
          startTime={lastPlaybackTimeRef.current}
          onError={handleStreamError}
          onPlay={onPlay}
          onPause={onPause}
          onEnded={onEnded}
          width="100%"
          height="100%"
          // responsive=true (the default) wraps the iframe in an
          // intrinsic-ratio (padding-top %) box that forces 16:9 from the
          // WIDTH, ignoring our fixed-height slot and overflowing it.
          //
          // responsive=false drops that wrapper's sizing entirely: stream-react
          // renders the wrapper <div> with `style: undefined`, so the iframe's
          // height="100%" has no definite parent height to resolve against and
          // collapses to the ~150px iframe default -- the player pins to the top
          // of the slot with a gray void below. `className` is applied to that
          // wrapper, so h-full/w-full give it the slot's dimensions and the
          // iframe's 100% now fills the carousel's h-[600px] slot; Cloudflare
          // letterboxes the video.
          className="h-full w-full"
          responsive={false}
        />
      </div>
    )
  }

  // Legacy YouTube/Vimeo path
  if (video.videoUrl) {
    return (
      <div className={`w-full h-full bg-black ${className ?? ""}`}>
        <ReactPlayer url={getEmbedUrl(video.videoUrl)} width="100%" height="100%" controls />
      </div>
    )
  }

  return <div className={`w-full h-full bg-black ${className ?? ""}`} />
}
